/**
 * Cmarkdownlink Component
 * Hyperlink component for markdown rendering — icon capsule + accent text with dotted underline
 */
'use client';
import type { ReactNode, CSSProperties } from 'react';
import { useState } from 'react';
import { IconLink } from '@tabler/icons-react';
import { Cicon } from '../Cicon';
import { INLINE_INHERIT_TYPOGRAPHY_STYLE } from '../Cmarkdown/inline-typography-style';
import { ColorType, getColorValue } from '@/theme/colors';
import { FontWeight } from '@/theme/typography';
import { useResolvedShortLink } from './useResolvedShortLink';
export interface CmarkdownlinkProps {
    children: ReactNode;
    href: string;
    title?: string;
    className?: string;
    style?: CSSProperties;
}
function splitFirstWord(children: ReactNode): {
    first: ReactNode;
    rest: ReactNode | null;
} {
    if (typeof children !== 'string') {
        return { first: children, rest: null };
    }
    const spaceIndex = children.indexOf(' ');
    if (spaceIndex === -1) {
        return { first: children, rest: null };
    }
    return { first: children.slice(0, spaceIndex), rest: children.slice(spaceIndex) };
}
export function Cmarkdownlink({ children, href, title, className, style, }: CmarkdownlinkProps) {
    const [isHovered, setIsHovered] = useState(false);
    // A research link arrives shortened; the anchor points at the real
    // destination so hover, copy-link and the status bar all show where it goes.
    const resolvedHref = useResolvedShortLink(href);
    const labelTextStyle: CSSProperties = {
        textDecorationLine: 'underline',
        textDecorationStyle: isHovered ? 'solid' : 'dotted',
        textUnderlineOffset: '3px',
        transition: 'text-decoration-style 0.15s ease',
    };
    const wrappedLabelTextStyle: CSSProperties = {
        ...labelTextStyle,
        maxWidth: '100%',
        minWidth: 0,
        flex: '1 1 auto',
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
    };
    const { first, rest } = splitFirstWord(children);
    return (
      <a href={resolvedHref} title={title} target="_blank" rel="noopener noreferrer" className={className} style={{
            display: 'inline-flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            maxWidth: '100%',
            textDecoration: 'none',
            cursor: 'pointer',
            ...style,
        }} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
        <span style={{ display: 'inline-flex', alignItems: 'center', minWidth: 0, maxWidth: '100%' }}>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            verticalAlign: 'middle',
            flexShrink: 0,
            margin: '0px 6px 3px 4px',
            padding: '5px',
            borderRadius: '20px',
            backgroundColor: getColorValue(ColorType.Accent, 0.29),
            boxShadow: isHovered
                ? '#ffffffa8 1px 1px 3px 0px inset, #ffffff47 -1px -1px 2px -1px inset, #00000080 0px 2px 3px 0px, #ffffff 0 -2px 2px 0px inset'
                : '#ffffffa8 1px 1px 3px 0px inset, #ffffff47 -1px -1px 2px -1px inset, #00000080 0px 2px 3px 0px, transparent 0 -2px 2px 0px inset',
            transition: 'box-shadow 0.3s ease',
        }}>
            <Cicon icon={IconLink} size={15} color={ColorType.White} opacity={0.95}/>
          </span>
          <span style={{
            fontWeight: FontWeight.Heavy,
            color: getColorValue(ColorType.Accent, 0.95),
            ...wrappedLabelTextStyle,
            ...INLINE_INHERIT_TYPOGRAPHY_STYLE,
        }}>
            {first}
          </span>
        </span>
        {rest && (<span style={{
                fontWeight: FontWeight.Heavy,
                color: getColorValue(ColorType.Accent, 0.95),
                ...wrappedLabelTextStyle,
                ...INLINE_INHERIT_TYPOGRAPHY_STYLE,
            }}>
            {rest}
          </span>)}
      </a>
    );
}
